import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSounds } from '../hooks/useSounds';
import { useVoice } from '../hooks/useVoice';
import { ConversationState } from '../hooks/useVoiceConversation';
import { MathText } from '../components/MathText';
import { MathEditor, MathEditorRef } from '../components/MathEditor';
import { GeometryDiagram, parseDiagramCommands } from '../components/GeometryDiagram';
import { CameraCapture } from '../components/CameraCapture';
import { api, QuestionResponse, AnswerResult, WorkedExampleResponse } from '../api';
import './Practice.css';

interface TutorMessage {
  sender: 'student' | 'ai';
  text: string;
  image?: string; // Base64 image data
}

// Convert LaTeX (with $ delimiters) to plain text for backend
const latexToPlain = (input: string): string => {
  // Remove $ delimiters and process LaTeX
  let result = input.replace(/\$/g, '');
  // \frac{a}{b} → (a)/(b)
  result = result.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1)/($2)');
  // x^{n} → x^n
  result = result.replace(/\^{([^}]*)}/g, '^$1');
  // \sqrt{x} → sqrt(x)
  result = result.replace(/\\sqrt\{([^}]*)\}/g, 'sqrt($1)');
  // \pi → pi
  result = result.replace(/\\pi/g, 'pi');
  // \times → *
  result = result.replace(/\\times/g, '*');
  // \div → /
  result = result.replace(/\\div/g, '/');
  // \pm → ±
  result = result.replace(/\\pm/g, '±');
  // \le → <=
  result = result.replace(/\\le/g, '<=');
  // \ge → >=
  result = result.replace(/\\ge/g, '>=');
  // \ne → !=
  result = result.replace(/\\ne/g, '!=');
  // Clean up remaining backslashes and braces
  result = result.replace(/\\/g, '');
  result = result.replace(/[{}]/g, '');
  return result.trim();
};

// Convert LaTeX to AFMF (Agent-Friendly Math Format) for AI consumption
// Uncomment when backend is updated to accept AFMF instead of plain text
// export const latexToAFMF = (input: string): string => {
//   return input
//     .replace(/\$\\frac\{([^}]+)\}\{([^}]+)\}\$/g, 'frac($1,$2)')
//     .replace(/\$([^$]*)\^\{([^}]+)\}([^$]*)\$/g, '$1pow($2)$3')
//     .replace(/\$\\sqrt\{([^}]+)\}\$/g, 'sqrt($1)')
//     .replace(/\$\\pi\$/g, 'pi')
//     .replace(/\$\\times\$/g, '*')
//     .replace(/\$([^$]+)\$/g, '$1');
// };

export default function Practice() {
  const { user } = useAuth(); // Get user for header display
  const { soundEnabled, toggleSound, playCorrect, playComplete, playIncorrect } = useSounds();
  const {
    voiceEnabled, ttsAvailable, sttAvailable,
    speak, isSpeaking, stopSpeaking,
    startStreamingSTT, stopStreamingSTT, isStreaming,
    isPaused, togglePauseStreaming
  } = useVoice();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Speech mode: off (no voice), listen (mic - turn-taking), full (headphones - simultaneous)
  type SpeechMode = 'off' | 'listen' | 'full';
  const [speechMode, setSpeechMode] = useState<SpeechMode>('off');

  // Top toggle: off ↔ headphones (default on state)
  const toggleVoiceOnOff = () => {
    setSpeechMode(prev => prev === 'off' ? 'full' : 'off');
  };

  // Bottom toggle: mic ↔ headphones (only when voice is on)
  const toggleMicHeadphones = () => {
    setSpeechMode(prev => prev === 'listen' ? 'full' : prev === 'full' ? 'listen' : prev);
  };
  const initialConceptId = searchParams.get('conceptId') ? parseInt(searchParams.get('conceptId')!) : undefined;

  const [sessionId, setSessionId] = useState<number | null>(null);
  const [question, setQuestion] = useState<QuestionResponse | null>(null);
  const [answer, setAnswer] = useState('');
  const [submittedAnswer, setSubmittedAnswer] = useState(''); // The answer shown in result card
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [targetConceptId, setTargetConceptId] = useState<number | undefined>(initialConceptId);

  // This Week mode state
  const [attemptCount, setAttemptCount] = useState(0);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [loadingFollowUp, setLoadingFollowUp] = useState(false);
  const [conceptMastered, setConceptMastered] = useState(false);
  const [lessonProgress, setLessonProgress] = useState(0); // Tracks consecutive correct (0, 1, or 2) from DB

  // Show Me mode - guided walkthrough that doesn't count toward completion
  const [showMeMode, setShowMeMode] = useState(false);
  const [seedQuestionId, setSeedQuestionId] = useState<number | null>(null); // Original question before Show Me

  // Question history for back/forward navigation (per lesson/concept)
  type QuestionState = 'working' | 'completed' | 'skipped';
  interface HistoryEntry {
    question: QuestionResponse;
    state: QuestionState;
    attempts: number;
    conceptId: number;
    isShowMe: boolean;      // Was this a Show Me question?
    isFollowUp?: boolean;   // Was this a follow-up question?
    wasCorrect?: boolean;   // Did they get it correct?
    answerGiven?: string;   // The answer they submitted
    dbId?: number;          // ID from database for updates
    dbPosition?: number;    // Position in database (for Show Me insertion)
  }
  const [questionHistory, setQuestionHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyIndexRef = useRef(historyIndex);

  // Keep ref in sync with state (fixes stale closure in callbacks)
  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  // Get history entries for current lesson only
  const currentConceptId = question?.concept?.id;
  const lessonHistory = questionHistory.filter(h => h.conceptId === currentConceptId);
  const lessonHistoryIndex = lessonHistory.findIndex(h => h.question.questionId === question?.questionId);

  // Worked Example modal state
  const [showWorkedExample, setShowWorkedExample] = useState(false);
  const [workedExample, setWorkedExample] = useState<WorkedExampleResponse | null>(null);
  const [loadingExample, setLoadingExample] = useState(false);

  // Tutor state
  const [showTutor, setShowTutor] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [tutorMessages, setTutorMessages] = useState<TutorMessage[]>([]);
  const [tutorInput, setTutorInput] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const [explanationVerified, setExplanationVerified] = useState(false); // NEW: Can't proceed until explanation is verified
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Voice Chat state (🎧 mode only)
  interface VoiceUtterance {
    sender: 'student' | 'sudo';
    text: string;
    timestamp: Date;
    chopId?: string;      // Exchange ID from server dedup
    difference?: string;  // Words that differed from last sent
    sent?: boolean;       // True if message has been sent to tutor
  }
  const [voiceMessages, setVoiceMessages] = useState<VoiceUtterance[]>([]);
  const [voiceConversationId, setVoiceConversationId] = useState<number | null>(null); // Separate from Sudo chat
  const [partialTranscript, setPartialTranscript] = useState('');
  const [pendingTranscript, setPendingTranscript] = useState(''); // Mic mode: accumulated transcript before Send
  const voiceChatEndRef = useRef<HTMLDivElement>(null);
  const [voiceChatExpanded, setVoiceChatExpanded] = useState(false); // Collapsed by default
  const lastSudoMessageRef = useRef<string>(''); // For echo detection
  // OLD: Debounce/dedup refs - replaced with proper "one request at a time" queuing
  // const lastSentTextRef = useRef<string>(''); // For deduplication
  // const lastSentTimeRef = useRef<number>(0); // For debounce
  const speechModeRef = useRef<SpeechMode>('off'); // Track mode without causing re-render
  const hasShownVoiceWarningRef = useRef(localStorage.getItem('voiceChatWarningShown') === 'true');

  // Simple similarity check for echo detection (Jaccard-ish)
  const calculateSimilarity = (a: string, b: string): number => {
    if (!a || !b) return 0;
    const wordsA = new Set(a.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    const wordsB = new Set(b.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    if (wordsA.size === 0 || wordsB.size === 0) return 0;
    const intersection = [...wordsA].filter(w => wordsB.has(w)).length;
    const union = new Set([...wordsA, ...wordsB]).size;
    return intersection / union;
  };

  // Voice conversation state machine (for future full integration)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [voiceConversationState, setVoiceConversationState] = useState<ConversationState>('IDLE');

  // Simple speak function - speaks whole text at once (no phrase chunking delay)
  const speakText = useCallback(async (text: string) => {
    if (speechMode === 'off') return;
    speak(text, true); // Force TTS - speechMode controls, not voiceEnabled
  }, [speechMode, speak]);

  // Voice auto-send to tutor (when user finishes speaking)
  // OLD: Debounce/dedup logic - replaced with proper "one request at a time" queuing
  // const now = Date.now();
  // if (now - lastSentTimeRef.current < 500) { console.log('Debounce: skipping'); return; }
  // if (text.trim() === lastSentTextRef.current) { console.log('Dedup: skipping'); return; }
  const sendVoiceToTutor = useCallback(async () => {
    if (!question?.questionId) return;

    // Get all unsent student messages to send as context
    const unsentMessages = voiceMessages.filter(m => m.sender === 'student' && !m.sent);
    if (unsentMessages.length === 0) return;

    // Combine all unsent chops into one message
    const combinedText = unsentMessages.map(m => m.text).join(' ');
    if (!combinedText.trim()) return;

    // Mark messages as sent BEFORE the request (prevents re-sending)
    setVoiceMessages(prev => prev.map(m =>
      m.sender === 'student' && !m.sent ? { ...m, sent: true } : m
    ));

    setTutorLoading(true);
    try {
      // Voice chat uses its own conversation (separate from Sudo chat)
      let convId = voiceConversationId;
      if (!convId) {
        const startRes = await api.startTutoring(question.questionId, 'voice');
        convId = startRes.conversationId;
        setVoiceConversationId(convId);
      }

      // Voice mode: request short, one-sentence responses
      const res = await api.sendTutorMessage(convId, combinedText.trim(), undefined, 'voice');

      // Split response by | delimiter into separate messages (for natural voice chunks)
      const chunks = res.message.split('|').map(s => s.trim()).filter(s => s);
      chunks.forEach(chunk => {
        setVoiceMessages(prev => [...prev, { sender: 'sudo', text: chunk, timestamp: new Date() }]);
      });

      lastSudoMessageRef.current = res.message; // Track for echo detection
      speakText(res.message.replace(/\|/g, ' ')); // Speak without delimiters
    } catch (err) { console.error('Voice tutor error:', err); }
    finally { setTutorLoading(false); }
  }, [voiceConversationId, question?.questionId, speakText, voiceMessages]);

  // Handle Send button in mic mode - send all accumulated chops
  const handleSendPendingTranscript = useCallback(() => {
    sendVoiceToTutor(); // Now handles accumulation internally
  }, [sendVoiceToTutor]);


  // OCR (photo of work) state
  const [ocrEnabled, setOcrEnabled] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null); // Base64 image preview for answer
  const [pendingChatImage, setPendingChatImage] = useState<string | null>(null); // Image to send with next chat message
  const [showCamera, setShowCamera] = useState(false); // PWA camera capture overlay
  const [cameraTarget, setCameraTarget] = useState<'answer' | 'chat'>('answer'); // Where to send captured image
  const [showWaitingForPhone, setShowWaitingForPhone] = useState(false); // PC waiting for phone photo
  const fileInputRef = useRef<HTMLInputElement>(null);
  const answerInputRef = useRef<MathEditorRef>(null);

  // Detect if this is likely a mobile device (phone/tablet)
  const isMobileDevice = () => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobileSize = window.innerWidth < 768;
    return isTouchDevice && isMobileSize;
  };

  // Store raw LaTeX from MathEditor (for display/storage)
  const [answerLatex, setAnswerLatex] = useState('');

  // Progress persistence key (per user, 1hr expiry)
  const PROGRESS_KEY = user?.id ? `kh_progress_${user.id}` : null;

  // Save progress to localStorage when it changes
  useEffect(() => {
    if (PROGRESS_KEY && (correctAnswers > 0 || questionsAnswered > 0)) {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify({
        correctAnswers,
        questionsAnswered,
        savedAt: Date.now()
      }));
    }
  }, [correctAnswers, questionsAnswered, PROGRESS_KEY]);

  const sessionStartedRef = useRef(false);
  useEffect(() => {
    // Check if URL has conceptId param but searchParams hasn't caught up yet
    const urlHasConceptId = window.location.search.includes('conceptId=');
    const searchParamsReady = !urlHasConceptId || initialConceptId !== undefined;

    // Use ref for synchronous check to prevent race conditions
    if (!sessionStartedRef.current && searchParamsReady) {
      sessionStartedRef.current = true;
      startSession();
    }
  }, [initialConceptId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [tutorMessages]);

  // Auto-scroll voice chat
  useEffect(() => {
    voiceChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [voiceMessages, partialTranscript]);

  // Check if OCR is available on mount
  useEffect(() => {
    api.getOcrStatus().then(res => setOcrEnabled(res.enabled)).catch(() => setOcrEnabled(false));
  }, []);

  // Poll for photos from other devices (phone camera → PC)
  // Polls faster when waiting modal is open
  useEffect(() => {
    if (!question?.questionId) return;

    const pollInterval = setInterval(async () => {
      try {
        const { photos } = await api.getPendingPhotos();
        if (photos && photos.length > 0) {
          // Apply the most recent photo
          const latestPhoto = photos[photos.length - 1];
          setCapturedImage(latestPhoto.imageBase64);
          if (latestPhoto.latex) {
            setAnswerLatex(latestPhoto.latex);
            setAnswer(latexToPlain(latestPhoto.latex));
          }
          // Clear pending photos after receiving
          await api.clearPendingPhotos();
          // Close the waiting modal if it's open
          setShowWaitingForPhone(false);
        }
      } catch (err) {
        // Silent fail - polling shouldn't interrupt the user
      }
    }, showWaitingForPhone ? 1500 : 3000); // Poll faster when waiting

    return () => clearInterval(pollInterval);
  }, [question?.questionId, showWaitingForPhone]);

  // Smart camera handler - uses phone if available, else PC webcam
  const openCamera = async (target: 'answer' | 'chat') => {
    setCameraTarget(target);

    if (isMobileDevice()) {
      // On mobile, use PWA camera directly
      setShowCamera(true);
    } else {
      // On PC, check if phone has been active recently (sent photos in last 5 min)
      try {
        const { photos } = await api.getPendingPhotos();
        // If there are already pending photos, apply them
        if (photos && photos.length > 0) {
          const latestPhoto = photos[photos.length - 1];
          setCapturedImage(latestPhoto.imageBase64);
          if (latestPhoto.latex) {
            setAnswerLatex(latestPhoto.latex);
            setAnswer(latexToPlain(latestPhoto.latex));
          }
          await api.clearPendingPhotos();
          return;
        }
      } catch (err) {
        // Ignore errors, fall through to webcam
      }

      // On PC without pending photos, show "waiting for phone" modal
      // User can cancel and use webcam instead
      setShowWaitingForPhone(true);
    }
  };

  // Use PC webcam directly (fallback)
  const useWebcam = () => {
    setShowWaitingForPhone(false);
    setShowCamera(true);
  };

  // Cancel waiting for phone
  const cancelWaitingForPhone = () => {
    setShowWaitingForPhone(false);
  };

  // Streaming STT callbacks - shared between initial start and reconnect on resume
  const getStreamingCallbacks = useCallback(() => ({
    onInterim: (text: string) => {
      setPartialTranscript(text);
    },
    onFinal: (text: string, metadata?: { chopId?: string; similarity?: number; difference?: string }) => {
      // Echo detection: check if transcript is similar to last Sudo message
      const similarity = calculateSimilarity(text, lastSudoMessageRef.current);
      if (similarity > 0.7) {
        console.log(`Echo detected (${(similarity * 100).toFixed(0)}% similar), discarding: "${text}"`);
        setPartialTranscript('');
        return;
      }
      if (metadata?.chopId) {
        console.log('Received chop [' + metadata.chopId + '] (' + ((metadata.similarity || 0) * 100).toFixed(0) + '% similar)');
      }
      // Add to voice messages - effect will auto-send in headphones mode
      setVoiceMessages(prev => [...prev, {
        sender: 'student',
        text,
        timestamp: new Date(),
        chopId: metadata?.chopId,
        difference: metadata?.difference
        // Note: sent defaults to undefined/false - will be marked true when sent
      }]);
      setPartialTranscript('');
    },
    onError: (error: string) => {
      console.error('Streaming STT error:', error);
    }
  }), []);

  // Track if we're in any voice mode (to avoid reconnecting when switching between mic/headphones)
  const isVoiceMode = speechMode !== 'off';
  const wasVoiceModeRef = useRef(false);

  // Handle Resume with experimental warning and reconnect if needed
  const handleVoiceResume = useCallback(() => {
    if (!hasShownVoiceWarningRef.current) {
      alert('Voice chat is experimental and still in development.');
      hasShownVoiceWarningRef.current = true;
      localStorage.setItem('voiceChatWarningShown', 'true');
    }

    // If WebSocket died during pause, reconnect
    if (!isStreaming && isVoiceMode && sttAvailable) {
      console.log('Voice WebSocket died during pause, reconnecting...');
      startStreamingSTT(getStreamingCallbacks());
      return;
    }

    togglePauseStreaming();
  }, [togglePauseStreaming, isStreaming, isVoiceMode, sttAvailable, startStreamingSTT, getStreamingCallbacks]);

  // Keep speechModeRef in sync (for use in callbacks without causing re-render)
  useEffect(() => {
    speechModeRef.current = speechMode;
  }, [speechMode]);

  // Start/stop streaming STT only when transitioning to/from voice mode
  useEffect(() => {
    const wasVoiceMode = wasVoiceModeRef.current;
    wasVoiceModeRef.current = isVoiceMode;

    if (isVoiceMode && sttAvailable && !wasVoiceMode) {
      // Starting voice mode - create connection
      startStreamingSTT(getStreamingCallbacks());
      // Start paused - user must click Resume to begin listening
      if (!isPaused) {
        togglePauseStreaming();
      }
    } else if (!isVoiceMode && wasVoiceMode) {
      // Leaving voice mode - close connection
      stopStreamingSTT();
      setPartialTranscript('');
      setPendingTranscript('');
    }
    // Note: switching between 'listen' and 'full' does NOT reconnect
  }, [isVoiceMode, sttAvailable, startStreamingSTT, stopStreamingSTT, getStreamingCallbacks]);

  // Headphones mode: auto-send when we have pending messages and no request in flight
  useEffect(() => {
    if (speechMode !== 'full') return;
    if (tutorLoading) return;

    const hasUnsent = voiceMessages.some(m => m.sender === 'student' && !m.sent);
    if (hasUnsent) {
      sendVoiceToTutor();
    }
  }, [tutorLoading, speechMode, voiceMessages, sendVoiceToTutor]);


  // File upload fallback handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setOcrLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        setCapturedImage(base64);

        // OCR the image
        try {
          const result = await api.ocrImage(base64, file.type);
          if (result.latex) {
            const recognized = result.latex.includes('$') ? result.latex : `$${result.latex}$`;
            setAnswerLatex(recognized);
            setAnswer(latexToPlain(recognized));
          }
        } catch (err) {
          console.error('OCR failed:', err);
        } finally {
          setOcrLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Failed to read file:', err);
      setOcrLoading(false);
    }
    e.target.value = '';
  };

  const handlePWACameraCapture = async (imageBase64: string) => {
    setShowCamera(false);
    setOcrLoading(true);

    try {
      if (cameraTarget === 'answer') {
        // Show preview
        setCapturedImage(imageBase64);
        // OCR the image
        try {
          const result = await api.ocrImage(imageBase64, 'image/jpeg');
          if (result.latex) {
            const recognized = result.latex.includes('$') ? result.latex : `$${result.latex}$`;
            setAnswerLatex(recognized);
            setAnswer(latexToPlain(recognized));
          }
        } catch (err) {
          console.error('OCR failed:', err);
        }
      } else {
        // Chat/tutor image
        setPendingChatImage(imageBase64);
        setTutorMessages(prev => [...prev, {
          sender: 'student',
          text: '[Photo of my work]',
          image: imageBase64
        }]);
        // OCR for context
        try {
          const result = await api.ocrImage(imageBase64, 'image/jpeg');
          if (result.latex) {
            const recognized = result.latex.includes('$') ? result.latex : `$${result.latex}$`;
            setTutorInput(`Here's my work: ${recognized}`);
          } else {
            setTutorInput("Here's my work - please look at the image.");
          }
        } catch (err) {
          console.error('OCR failed:', err);
          setTutorInput("Here's my work - please look at the image.");
        }
      }
    } finally {
      setOcrLoading(false);
    }
  };

  // Load persisted question history from database
  const loadPersistedHistory = useCallback(async (conceptId: number) => {
    try {
      const { history } = await api.getQuestionHistory(conceptId);
      if (history.length > 0) {
        const entries: HistoryEntry[] = history.map((h) => ({
          question: {
            questionId: h.questionId,
            questionText: h.questionText || '',
            concept: {
              id: h.conceptId,
              lessonNumber: h.lessonNumber || 0,
              title: h.title || '',
              completionCriteria: h.completionCriteria,
              totalQuestions: h.totalQuestions,
              totalCompleted: h.totalCompleted,
            },
          } as QuestionResponse,
          state: h.state,
          attempts: h.attempts,
          conceptId: h.conceptId,
          isShowMe: h.isShowMe || false,
          isFollowUp: false, // Follow-up state is runtime, not persisted
          wasCorrect: h.isCorrect,
          answerGiven: h.answerGiven,
          dbId: h.id,
          dbPosition: h.position,
        }));
        setQuestionHistory(entries);
        // Start at the last working question or most recent
        const lastWorkingIdx = entries.findIndex(e => e.state === 'working');
        setHistoryIndex(lastWorkingIdx >= 0 ? lastWorkingIdx : entries.length - 1);
        return entries;
      }
      return [];
    } catch (error) {
      console.error('Failed to load history:', error);
      return [];
    }
  }, []);

  const startSession = async () => {
    try {
      // Restore progress from localStorage if within 1 hour
      if (PROGRESS_KEY) {
        const saved = localStorage.getItem(PROGRESS_KEY);
        if (saved) {
          const { correctAnswers: savedCorrect, questionsAnswered: savedAnswered, savedAt } = JSON.parse(saved);
          const oneHour = 60 * 60 * 1000;
          if (Date.now() - savedAt < oneHour) {
            setCorrectAnswers(savedCorrect);
            setQuestionsAnswered(savedAnswered);
          } else {
            localStorage.removeItem(PROGRESS_KEY); // Expired
          }
        }
      }

      const session = await api.startSession();
      setSessionId(session.sessionId);

      // Step 1: Determine conceptId
      // - From URL (card click): use initialConceptId
      // - From "Start Practice Session": ask backend for next concept
      let conceptId = initialConceptId;
      let freshQuestion: QuestionResponse | null = null;

      if (!conceptId) {
        // No concept in URL - ask backend which concept to work on
        freshQuestion = await api.getNextQuestion();
        conceptId = freshQuestion.concept?.id;
      }

      if (!conceptId) {
        // No concept available - show the fresh question or error
        if (freshQuestion) {
          setQuestion(freshQuestion);
          setLoading(false);
        }
        return;
      }

      // Step 2: Load persisted history for this concept
      const persistedHistory = await loadPersistedHistory(conceptId);

      if (persistedHistory.length > 0) {
        // Step 3a: Resume from history
        try {
          const progress = await api.getHistoryProgress(conceptId);
          setLessonProgress(progress.consecutiveCorrect || 0);
          // Check mastery based on completion criteria
          const criteria = persistedHistory[0]?.question?.concept?.completionCriteria;
          const totalQ = persistedHistory[0]?.question?.concept?.totalQuestions || 0;
          const totalC = persistedHistory[0]?.question?.concept?.totalCompleted || 0;
          if (criteria === 'complete_all') {
            if (totalC >= totalQ && totalQ > 0) {
              setConceptMastered(true);
            }
          } else if (progress.consecutiveCorrect >= 2) {
            setConceptMastered(true);
          }
        } catch (err) {
          console.error('Failed to load progress:', err);
        }

        // For complete_all mode, ensure all seed questions are in history
        // This handles cases where history was partially loaded in previous sessions
        const resumeCriteria = persistedHistory[0]?.question?.concept?.completionCriteria;
        const resumeTotalQ = persistedHistory[0]?.question?.concept?.totalQuestions || 0;
        if (resumeCriteria === 'complete_all' && resumeTotalQ > 0 && conceptId) {
          // Count seed questions in persisted history (non-ShowMe, non-generated entries)
          const seedQuestionIds = new Set(persistedHistory.filter(h => !h.isShowMe).map(h => h.question.questionId));

          if (seedQuestionIds.size < resumeTotalQ) {
            try {
              const { questions: allQuestions } = await api.getConceptQuestions(conceptId);
              let addedAny = false;

              // Add missing questions to history
              for (const aq of allQuestions) {
                if (!aq.questionId || seedQuestionIds.has(aq.questionId)) continue;

                const { entry: dbEntry } = await api.addToQuestionHistory(conceptId, aq.questionId, false);
                persistedHistory.push({
                  question: aq,
                  state: aq.isCompleted ? 'completed' : 'working',
                  wasCorrect: aq.isCompleted ? true : undefined,
                  attempts: aq.isCompleted ? 1 : 0,
                  conceptId: conceptId,
                  isShowMe: false,
                  isFollowUp: false,
                  dbId: dbEntry.id,
                  dbPosition: dbEntry.position
                });
                addedAny = true;
              }

              if (addedAny) {
                // Sort by DB position and update state
                persistedHistory.sort((a, b) => (a.dbPosition || 0) - (b.dbPosition || 0));
                setQuestionHistory(persistedHistory);
              }
            } catch (err) {
              console.error('Failed to pre-load missing questions:', err);
            }
          }
        }

        // Resume at the first unanswered non-ShowMe question
        const lastWorkingIdx = persistedHistory.findIndex(e => e.state === 'working' && !e.isShowMe);
        // If no non-ShowMe working question, fall back to any working question, then last entry
        const fallbackIdx = lastWorkingIdx < 0 ? persistedHistory.findIndex(e => e.state === 'working') : lastWorkingIdx;
        const resumeIdx = fallbackIdx >= 0 ? fallbackIdx : persistedHistory.length - 1;
        const entry = persistedHistory[resumeIdx];
        setQuestion(entry.question);
        setLoading(false);
        if (entry.isFollowUp) setIsFollowUp(true);
        setShowMeMode(entry.isShowMe || false); // Restore Show Me mode from history
        setAttemptCount(entry.attempts);

        // Load conversation history and restore full state
        if (entry.question.questionId) {
          try {
            const conv = await api.getConversation(entry.question.questionId);
            if (conv.messages.length > 0) {
              setConversationId(conv.conversationId);
              setTutorMessages(conv.messages.map(m => ({ sender: m.sender as 'student' | 'ai', text: m.text })));
              setShowTutor(true);

              // Get first student message as "Your answer" for result card (original answer, not follow-ups)
              const studentMessages = conv.messages.filter(m => m.sender === 'student');
              const firstStudentMessage = studentMessages[0];
              if (firstStudentMessage) {
                setSubmittedAnswer(firstStudentMessage.text);
              }

              // Get last AI message as feedback for result card
              const aiMessages = conv.messages.filter(m => m.sender === 'ai');
              const lastAiMessage = aiMessages[aiMessages.length - 1];
              if (lastAiMessage) {
                setResult({
                  isCorrect: entry.wasCorrect || false,
                  feedback: lastAiMessage.text,
                  xpEarned: 0
                });
                if (entry.state === 'completed') {
                  setExplanationVerified(true);
                }
              }
            }
          } catch (err) {
            console.error('Failed to load conversation:', err);
          }
        } else if (entry.state === 'completed') {
          // No conversation but completed - show basic result
          setResult({ isCorrect: entry.wasCorrect || false, feedback: '', xpEarned: 0 });
          setExplanationVerified(true);
        }
        return;
      }

      // Step 3b: No history - load fresh question
      const q = freshQuestion || await api.getNextQuestion(conceptId);
      setQuestion(q);
      setSeedQuestionId(q.questionId ?? null);
      setLoading(false);

      // Initialize lesson progress from the question response
      const consecutiveCorrect = q.concept?.consecutiveCorrect || 0;
      setLessonProgress(consecutiveCorrect);
      // Check mastery based on completion criteria
      const criteria = q.concept?.completionCriteria;
      if (criteria === 'complete_all') {
        // For complete_all, mastery requires all questions completed
        const totalQ = q.concept?.totalQuestions || 0;
        const totalC = q.concept?.totalCompleted || 0;
        if (totalC >= totalQ && totalQ > 0) {
          setConceptMastered(true);
        }

        // PRE-LOAD ALL QUESTIONS for complete_all mode
        // This allows arrow navigation through the entire lesson
        if (conceptId && totalQ > 0) {
          try {
            const { questions: allQuestions } = await api.getConceptQuestions(conceptId);
            const newHistory: HistoryEntry[] = [];

            for (let i = 0; i < allQuestions.length; i++) {
              const aq = allQuestions[i];
              if (!aq.questionId) continue;

              // Add to DB history
              const { entry: dbEntry } = await api.addToQuestionHistory(conceptId, aq.questionId, false);

              newHistory.push({
                question: aq,
                state: aq.isCompleted ? 'completed' : 'working',
                wasCorrect: aq.isCompleted ? true : undefined,
                attempts: aq.isCompleted ? 1 : 0,
                conceptId: conceptId,
                isShowMe: false,
                isFollowUp: false,
                dbId: dbEntry.id,
                dbPosition: dbEntry.position
              });
            }

            setQuestionHistory(newHistory);
            // Start at first unanswered non-ShowMe question
            const firstUnanswered = newHistory.findIndex(h => h.state !== 'completed' && !h.isShowMe);
            const startIdx = firstUnanswered >= 0 ? firstUnanswered : 0;
            setHistoryIndex(startIdx);
            setQuestion(newHistory[startIdx].question);
          } catch (err) {
            console.error('Failed to pre-load complete_all questions:', err);
            // Fallback: just use the single question
            if (q.questionId && conceptId) {
              const { entry: dbEntry } = await api.addToQuestionHistory(conceptId, q.questionId, false);
              setQuestionHistory([{
                question: q,
                state: 'working',
                attempts: 0,
                conceptId: conceptId,
                isShowMe: false,
                isFollowUp: false,
                dbId: dbEntry.id,
                dbPosition: dbEntry.position
              }]);
              setHistoryIndex(0);
            }
          }
        }
      } else {
        // two_first_try logic
        if (consecutiveCorrect >= 2) {
          setConceptMastered(true);
          setIsFollowUp(true);
        } else if (consecutiveCorrect >= 1) {
          setIsFollowUp(true);
        }

        // Add single question to history for two_first_try mode
        if (q.questionId && conceptId) {
          const isDuplicate = questionHistory.some(h => h.question.questionId === q.questionId);
          if (!isDuplicate) {
            try {
              const { entry: dbEntry } = await api.addToQuestionHistory(conceptId, q.questionId, false);
              setQuestionHistory(prev => [...prev, {
                question: q,
                state: 'working',
                attempts: 0,
                conceptId: conceptId,
                isShowMe: false,
                isFollowUp: false,
                dbId: dbEntry.id,
                dbPosition: dbEntry.position
              }]);
              setHistoryIndex(questionHistory.length);
            } catch (err) {
              console.error('Failed to persist question to history:', err);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to start session:', err);
    }
  };

  const loadNextQuestion = async (conceptId?: number, afterQuestionId?: number) => {
    setLoading(true);
    setResult(null);
    setAnswer('');
    setAnswerLatex('');
    setSubmittedAnswer('');
    setShowTutor(false);
    setConversationId(null);
    setTutorMessages([]);
    setAttemptCount(0);
    setIsFollowUp(false);
    setConceptMastered(false);
    setShowWorkedExample(false);
    setWorkedExample(null);
    setExplanationVerified(false); // Reset for new question
    setCapturedImage(null); // Clear any captured photo
    setPendingChatImage(null);
    setShowMeMode(false); // Exit Show Me mode when loading new question
    setSeedQuestionId(null); // Will be set to new question's ID below

    try {
      // Use specific concept for first question, then let system choose
      // If afterQuestionId is provided, get the next question after that ID (for arrow navigation)
      const q = await api.getNextQuestion(conceptId || targetConceptId, afterQuestionId);
      setQuestion(q);
      setSeedQuestionId(q.questionId ?? null); // This is the seed question

      // Initialize lesson progress from database
      const consecutiveCorrect = q.concept?.consecutiveCorrect || 0;
      setLessonProgress(consecutiveCorrect);
      // Check mastery based on completion criteria
      const criteria = q.concept?.completionCriteria;
      if (criteria === 'complete_all') {
        const totalQ = q.concept?.totalQuestions || 0;
        const totalC = q.concept?.totalCompleted || 0;
        if (totalC >= totalQ && totalQ > 0) {
          setConceptMastered(true);
        }
      } else {
        if (consecutiveCorrect >= 2) {
          setConceptMastered(true);
          setIsFollowUp(true);
        } else if (consecutiveCorrect >= 1) {
          setIsFollowUp(true);
        }
      }

      // Check if already in history (avoid duplicates)
      const isDuplicate = questionHistory.some(h => h.question.questionId === q.questionId);
      if (!isDuplicate && q.questionId && q.concept?.id) {
        // Persist to database
        try {
          const { entry: dbEntry } = await api.addToQuestionHistory(
            q.concept.id,
            q.questionId,
            false // isShowMe
          );

          // Add to local history with DB ID and position
          setQuestionHistory(prev => {
            const newEntry: HistoryEntry = {
              question: q,
              state: 'working',
              attempts: 0,
              conceptId: q.concept?.id || 0,
              isShowMe: false,
              isFollowUp: false,
              dbId: dbEntry.id,
              dbPosition: dbEntry.position
            };
            return [...prev, newEntry];
          });
        } catch (err) {
          console.error('Failed to persist question to history:', err);
          // Still add to local history even if DB fails
          setQuestionHistory(prev => {
            const newEntry: HistoryEntry = {
              question: q,
              state: 'working',
              attempts: 0,
              conceptId: q.concept?.id || 0,
              isShowMe: false,
              isFollowUp: false
            };
            return [...prev, newEntry];
          });
        }
      }
      setHistoryIndex(questionHistory.length); // Point to new entry

      // Clear target after first use so "Next Question" picks naturally
      if (targetConceptId) setTargetConceptId(undefined);
    } catch (err) {
      console.error('Failed to load question:', err);
    } finally {
      setLoading(false);
    }
  };

  // Navigate back in lesson history (same concept only)
  const goBack = async () => {
    if (lessonHistoryIndex > 0) {
      const entry = lessonHistory[lessonHistoryIndex - 1];
      // Find the global index for this entry
      const globalIndex = questionHistory.findIndex(h => h.question.questionId === entry.question.questionId);
      setHistoryIndex(globalIndex);
      setQuestion(entry.question);
      // Show result for completed questions
      if (entry.state === 'completed' && entry.wasCorrect) {
        setResult({ isCorrect: true, feedback: '', xpEarned: 0 });
        setExplanationVerified(true);
      } else {
        setResult(null);
        setExplanationVerified(false);
      }
      // Restore follow-up state (must explicitly set to false if not follow-up)
      setIsFollowUp(entry.isFollowUp || false);
      setAnswer('');
      setAnswerLatex('');
      setAttemptCount(entry.attempts);
      // Set Show Me mode based on entry type
      if (entry.isShowMe) {
        setShowMeMode(true);
      } else {
        setShowMeMode(false);
        setSeedQuestionId(entry.question.questionId ?? null);
      }
      // Load conversation history for this question
      if (entry.question.questionId) {
        try {
          const conv = await api.getConversation(entry.question.questionId);
          setConversationId(conv.conversationId);
          setTutorMessages(conv.messages.map(m => ({ sender: m.sender as 'student' | 'ai', text: m.text })));
          setShowTutor(conv.messages.length > 0);

          // Restore result from conversation
          if (conv.messages.length > 0) {
            const studentMessages = conv.messages.filter(m => m.sender === 'student');
            const aiMessages = conv.messages.filter(m => m.sender === 'ai');
            // Last student message is the final answer after Sudo chat, last AI message is latest feedback
            const lastStudentMessage = studentMessages[studentMessages.length - 1];
            const lastAiMessage = aiMessages[aiMessages.length - 1];

            if (lastStudentMessage) {
              setSubmittedAnswer(lastStudentMessage.text);
            }
            if (lastAiMessage) {
              setResult({
                isCorrect: entry.wasCorrect || false,
                feedback: lastAiMessage.text,
                xpEarned: 0
              });
            }
          }
        } catch (err) {
          console.error('Failed to load conversation:', err);
          setConversationId(null);
          setTutorMessages([]);
          setShowTutor(false);
        }
      }
    }
  };

  // Navigate forward in lesson history (same concept only)
  // All questions are pre-loaded for complete_all mode, so just navigate
  const goForward = async () => {
    // If at end of history, stay there
    if (lessonHistoryIndex >= lessonHistory.length - 1) {
      return;
    }

    if (lessonHistoryIndex < lessonHistory.length - 1) {
      const entry = lessonHistory[lessonHistoryIndex + 1];
      // Find the global index for this entry
      const globalIndex = questionHistory.findIndex(h => h.question.questionId === entry.question.questionId);
      setHistoryIndex(globalIndex);
      setQuestion(entry.question);
      // Show result for completed questions
      if (entry.state === 'completed' && entry.wasCorrect) {
        setResult({ isCorrect: true, feedback: '', xpEarned: 0 });
        setExplanationVerified(true);
      } else {
        setResult(null);
        setExplanationVerified(false);
      }
      // Restore follow-up state (must explicitly set to false if not follow-up)
      setIsFollowUp(entry.isFollowUp || false);
      setAnswer('');
      setAnswerLatex('');
      setAttemptCount(entry.attempts);
      // Set Show Me mode based on entry type
      if (entry.isShowMe) {
        setShowMeMode(true);
      } else {
        setShowMeMode(false);
        setSeedQuestionId(entry.question.questionId ?? null);
      }
      // Load conversation history for this question
      if (entry.question.questionId) {
        try {
          const conv = await api.getConversation(entry.question.questionId);
          setConversationId(conv.conversationId);
          setTutorMessages(conv.messages.map(m => ({ sender: m.sender as 'student' | 'ai', text: m.text })));
          setShowTutor(conv.messages.length > 0);

          // Restore result from conversation
          if (conv.messages.length > 0) {
            const studentMessages = conv.messages.filter(m => m.sender === 'student');
            const aiMessages = conv.messages.filter(m => m.sender === 'ai');
            // Last student message is the final answer after Sudo chat, last AI message is latest feedback
            const lastStudentMessage = studentMessages[studentMessages.length - 1];
            const lastAiMessage = aiMessages[aiMessages.length - 1];

            if (lastStudentMessage) {
              setSubmittedAnswer(lastStudentMessage.text);
            }
            if (lastAiMessage) {
              setResult({
                isCorrect: entry.wasCorrect || false,
                feedback: lastAiMessage.text,
                xpEarned: 0
              });
            }
          }
        } catch (err) {
          console.error('Failed to load conversation:', err);
          setConversationId(null);
          setTutorMessages([]);
          setShowTutor(false);
        }
      }
    }
  };

  // Check if navigation is available within current lesson
  const canGoBack = lessonHistoryIndex > 0;
  // Can go forward if: 1) more questions in history ahead, OR 2) at end of history but lesson has more questions
  const totalQuestionsInLesson = question?.concept?.totalQuestions || 0;
  const isCompleteAllMode = question?.concept?.completionCriteria === 'complete_all';
  const hasMoreQuestionsInLesson = isCompleteAllMode && lessonHistory.length < totalQuestionsInLesson;
  const canGoForwardInHistory = lessonHistoryIndex >= 0 && lessonHistoryIndex < lessonHistory.length - 1;
  const canGoForward = canGoForwardInHistory || (lessonHistoryIndex === lessonHistory.length - 1 && hasMoreQuestionsInLesson);

  // Update current question state in history (local + database)
  const updateHistoryState = (state: QuestionState, attempts?: number, wasCorrect?: boolean, answer?: string) => {
    const entry = questionHistory[historyIndex];
    if (!entry) return;

    // Update local state
    setQuestionHistory(prev => {
      const updated = [...prev];
      if (updated[historyIndex]) {
        updated[historyIndex] = {
          ...updated[historyIndex],
          state,
          attempts: attempts ?? updated[historyIndex].attempts,
          wasCorrect: wasCorrect ?? updated[historyIndex].wasCorrect,
          answerGiven: answer ?? updated[historyIndex].answerGiven,
          isFollowUp: isFollowUp // Capture current follow-up state
        };
      }
      return updated;
    });

    // Persist to database
    if (entry.question.questionId && entry.conceptId) {
      api.updateQuestionHistory(entry.conceptId, entry.question.questionId, {
        state,
        attempts: attempts ?? entry.attempts,
        isCorrect: wasCorrect,
        answerGiven: answer
      }).catch(err => {
        console.error('Failed to persist history state:', err);
      });
    }
  };

  const loadFollowUpQuestion = async () => {
    if (!question?.questionId || !question?.concept?.id) return;

    setLoadingFollowUp(true);
    try {
      const followUp = await api.getFollowUpProblem(question.questionId);
      const newQuestion = {
        questionId: followUp.questionId,
        questionText: followUp.questionText,
        concept: question.concept // Keep the same concept info
      };
      setQuestion(newQuestion);
      setResult(null);
      setAnswer('');
      setAnswerLatex('');
      setAttemptCount(0);
      setIsFollowUp(true);
      setShowTutor(false);
      setConversationId(null);
      setTutorMessages([]);
      setShowWorkedExample(false);
      setWorkedExample(null);
      setExplanationVerified(false); // Reset for follow-up

      // Add to history (same as loadNextQuestion)
      if (followUp.questionId) {
        try {
          const { entry: dbEntry } = await api.addToQuestionHistory(
            question.concept.id,
            followUp.questionId,
            false // isShowMe
          );

          setQuestionHistory(prev => {
            const newEntry: HistoryEntry = {
              question: newQuestion,
              state: 'working',
              attempts: 0,
              conceptId: question.concept?.id || 0,
              isShowMe: false,
              isFollowUp: true,
              dbId: dbEntry.id,
              dbPosition: dbEntry.position
            };
            return [...prev, newEntry];
          });
          setHistoryIndex(questionHistory.length); // Point to new entry
        } catch (err) {
          console.error('Failed to persist follow-up to history:', err);
          // Still add to local history
          setQuestionHistory(prev => {
            const newEntry: HistoryEntry = {
              question: newQuestion,
              state: 'working',
              attempts: 0,
              conceptId: question.concept?.id || 0,
              isShowMe: false,
              isFollowUp: true
            };
            return [...prev, newEntry];
          });
          setHistoryIndex(questionHistory.length);
        }
      }
    } catch (err) {
      console.error('Failed to load follow-up:', err);
    } finally {
      setLoadingFollowUp(false);
    }
  };

  // Skip current question but stay in the same lesson/concept
  const handleSkip = async () => {
    if (!question?.concept?.id) return;
    // Pass the current concept ID to stay in the same lesson
    await loadNextQuestion(question.concept.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question?.questionId || !answer.trim()) return;

    setSubmitting(true);
    console.log('[handleSubmit] answerLatex:', answerLatex, 'answer:', answer);
    try {
      const res = await api.submitAnswer(question.questionId, answer, sessionId || undefined);
      console.log('[handleSubmit] Setting submittedAnswer to:', answerLatex);
      setSubmittedAnswer(answerLatex); // Save for display in result card
      setResult(res);
      setQuestionsAnswered(prev => prev + 1);

      // Clear input for next attempt (if wrong) or explanation (if correct)
      setAnswer('');
      setAnswerLatex('');

      const currentAttempt = (res.attemptCount || attemptCount + 1);
      setAttemptCount(currentAttempt);

      if (res.isCorrect) {
        setCorrectAnswers(prev => prev + 1);
        // Mark question as completed in history (unless in Show Me mode)
        if (!showMeMode) {
          updateHistoryState('completed', currentAttempt, true, answerLatex);
        }

        // Check if this was first attempt correct
        const criteria = question.concept?.completionCriteria;
        if (currentAttempt === 1 && !showMeMode) {
          // Increment lesson progress for first-attempt correct
          setLessonProgress(prev => prev + 1);
          if (criteria === 'complete_all') {
            // For complete_all, check if all questions now completed
            const totalQ = question.concept?.totalQuestions || 0;
            const totalC = (question.concept?.totalCompleted || 0) + 1; // +1 for this answer
            if (totalC >= totalQ && totalQ > 0) {
              setConceptMastered(true);
              playComplete();
            } else {
              playCorrect();
            }
          } else if (isFollowUp) {
            // Second consecutive first-attempt correct = concept mastered!
            setConceptMastered(true);
            playComplete(); // Celebratory 3-tone sound
          } else {
            playCorrect(); // 2-tone correct sound
          }
        } else {
          playCorrect(); // Still play correct sound even if not first attempt
        }
      } else {
        // Wrong answer - play oops sound only on first wrong
        if (attemptCount === 0) {
          playIncorrect();
        }
        // Update attempt count in history (save last answer)
        if (!showMeMode) {
          updateHistoryState('working', currentAttempt, false, answerLatex);
        }
      }

      // UNIFIED: Tutor response comes with submit - no separate startTutoring call
      if (res.conversationId) {
        setConversationId(res.conversationId);
      }
      const tutorMessage = res.message || res.feedback;
      if (tutorMessage) {
        // Add student's answer AND tutor's response to chat
        setTutorMessages(prev => [
          ...prev,
          { sender: 'student', text: answerLatex || answer },
          { sender: 'ai', text: tutorMessage }
        ]);
        setShowTutor(true);

        // Add Sudo's response to voice chat (if in voice mode)
        if (speechMode !== 'off') {
          setVoiceMessages(prev => [...prev, {
            sender: 'sudo',
            text: tutorMessage,
            timestamp: new Date()
          }]);
          lastSudoMessageRef.current = tutorMessage; // Track for echo detection
          // Speak the response using phrase-based TTS
          speakText(tutorMessage);
        }
      }
      // Set explanation verified based on backend's determination
      if (res.needsExplanation === false) {
        setExplanationVerified(true);
      } else {
        // Tutor asked a question - focus answer field for response
        setTimeout(() => answerInputRef.current?.focus(), 100);
      }
    } catch (err: any) {
      console.error('Failed to submit answer:', err);
      // Show user-friendly error message
      const errorMessage = err?.message || 'Something went wrong. Please try again.';
      setResult({
        isCorrect: false,
        feedback: errorMessage,
        xpEarned: 0
      });
    } finally {
      setSubmitting(false);
    }
  };

  const loadWorkedExample = async () => {
    if (!question?.questionId) return;

    setLoadingExample(true);
    try {
      const example = await api.getWorkedExample(question.questionId);
      setWorkedExample(example);
      setShowWorkedExample(true);
    } catch (err) {
      console.error('Failed to load worked example:', err);
    } finally {
      setLoadingExample(false);
    }
  };

  // Show Me mode - load a new related question for guided walkthrough (doesn't count toward completion)
  // Show Me mode - max 2 per seed question (count Show Me entries in history after the seed)
  const currentSeedId = seedQuestionId ?? question?.questionId;
  const showMeCount = (() => {
    if (!currentSeedId) return 0;
    // Find the seed question's index in history
    const seedIdx = questionHistory.findIndex(h => h.question.questionId === currentSeedId);
    if (seedIdx === -1) return 0;
    // Count consecutive isShowMe entries after the seed
    let count = 0;
    for (let i = seedIdx + 1; i < questionHistory.length; i++) {
      if (questionHistory[i].isShowMe) count++;
      else break; // Stop at first non-Show-Me question
    }
    return count;
  })();
  const canShowMe = showMeCount < 2;

  const handleShowMe = async () => {
    if (!question?.concept?.id || !canShowMe) return;

    setLoading(true);
    try {
      // Track seed question if not already set
      if (!seedQuestionId) {
        setSeedQuestionId(question.questionId ?? null);
      }

      // Get current position using ref (avoids stale closure)
      const currentArrayIndex = historyIndexRef.current;
      // Get the DB position from the current entry (not the array index!)
      const currentEntry = questionHistory[currentArrayIndex];
      const currentDbPosition = currentEntry?.dbPosition ?? currentArrayIndex;

      // Generate a new similar question for walkthrough (Mode 1)
      const similarResponse = await api.getSimilarProblem(question.questionId!);

      // Construct a full question object, preserving concept from original
      const q = {
        questionId: similarResponse.questionId,
        questionText: similarResponse.questionText,
        concept: question.concept, // Keep original concept info
        isSimilarProblem: true,
        originalQuestionId: similarResponse.originalQuestionId
      };
      setQuestion(q);
      setResult(null);
      setAnswer('');
      setAnswerLatex('');
      setShowMeMode(true); // Enter Show Me mode
      // Count is derived from history (no need to track separately)
      setShowTutor(true); // Show tutor to guide through
      setConversationId(null);
      setTutorMessages([]);
      setShowWorkedExample(false);
      setWorkedExample(null);
      setCapturedImage(null);
      setPendingChatImage(null);

      // Add Show Me question to history (insert after current DB position)
      if (q.questionId && q.concept?.id) {
        try {
          const { entry: dbEntry } = await api.addToQuestionHistory(
            q.concept.id,
            q.questionId,
            true, // isShowMe
            currentDbPosition // afterPosition - use DB position, not array index
          );

          // Insert into local history after current array index
          setQuestionHistory(prev => {
            const newEntry: HistoryEntry = {
              question: q,
              state: 'working',
              attempts: 0,
              conceptId: q.concept?.id || 0,
              isShowMe: true,
              isFollowUp: false,
              dbId: dbEntry.id,
              dbPosition: dbEntry.position
            };
            const updated = [...prev];
            updated.splice(currentArrayIndex + 1, 0, newEntry);
            return updated;
          });
          setHistoryIndex(currentArrayIndex + 1);
        } catch (err) {
          console.error('Failed to persist Show Me question:', err);
        }
      }
    } catch (err) {
      console.error('Failed to load Show Me question:', err);
    } finally {
      setLoading(false);
    }
  };

  // Flag for Later - marks this single question as skipped and notifies teacher/parent
  const handleFlagForLater = async () => {
    if (!question?.questionId || !question?.concept?.id) return;

    try {
      // Mark as skipped in local history
      updateHistoryState('skipped', attemptCount);

      // Save to database and notify teacher/parent
      await api.skipQuestion(question.questionId, question.concept.id, attemptCount);

      // Move to next question
      await loadNextQuestion(question.concept.id);
    } catch (err) {
      console.error('Failed to flag for later:', err);
    }
  };

  const sendTutorMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversationId || !tutorInput.trim()) return;

    const message = tutorInput.trim();
    const imageToSend = pendingChatImage; // Capture before clearing
    setTutorInput('');
    setPendingChatImage(null); // Clear pending image
    setTutorMessages(prev => [...prev, { sender: 'student', text: message }]);
    setSubmittedAnswer(message); // Update "You:" in the result card with latest message
    setTutorLoading(true);

    try {
      // Send message with image if available (for Claude to analyze the work)
      // Mode 1 (walkthrough) for Show Me questions, Mode 2 (demonstrate) for all others
      const conversationMode = showMeMode ? 'walkthrough' : 'demonstrate';
      const res = await api.sendTutorMessage(conversationId, message, imageToSend || undefined, conversationMode);
      setTutorMessages(prev => [...prev, { sender: 'ai', text: res.message }]);

      // Update the result feedback to show latest Sudo response in the green/red card
      setResult(prev => prev ? { ...prev, feedback: res.message } : null);

      // Add Sudo's response to voice chat (if in voice mode)
      if (speechMode !== 'off') {
        setVoiceMessages(prev => [...prev, {
          sender: 'sudo',
          text: res.message,
          timestamp: new Date()
        }]);
        lastSudoMessageRef.current = res.message; // Track for echo detection
        // Speak the response using phrase-based TTS
        speakText(res.message);
      }

      // Check if explanation is satisfactory
      // Heuristic: If tutor response contains positive indicators, verify explanation
      const positiveIndicators = [
        'great explanation', 'good thinking', 'exactly right', 'that makes sense',
        'you understand', 'well done', 'nice work', 'perfect', 'excellent',
        'you got it', 'that\'s correct', 'good job explaining', 'clear explanation'
      ];
      const responseLC = res.message.toLowerCase();
      const isPositiveResponse = positiveIndicators.some(p => responseLC.includes(p));

      // Also check: if they gave a real explanation (>20 chars) and result was correct
      const gaveRealExplanation = message.length > 20 && !message.match(/^[0-9\s\+\-\*\/\=]+$/);

      const shouldAutoAdvance = isPositiveResponse || (gaveRealExplanation && result?.isCorrect && tutorMessages.length >= 2);

      if (shouldAutoAdvance) {
        setExplanationVerified(true);
        // Auto-advance: go to follow-up, next question, or dashboard
        const isComplete = question?.concept?.completionCriteria === 'complete_all';
        setTimeout(() => {
          if (isFollowUp || conceptMastered) {
            navigate('/dashboard');
          } else if (isComplete) {
            // For complete_all: just go to next seed question, no follow-up
            canGoForward ? goForward() : loadNextQuestion();
          } else {
            loadFollowUpQuestion();
          }
        }, 100);
      } else if (res.isResolved) {
        // Student got it right with tutor help
        setResult({ isCorrect: true, feedback: 'Great job working through it!', xpEarned: 5 });
        setExplanationVerified(true);
        // Play celebration if they persevered (>3 attempts), otherwise regular correct
        if (attemptCount > 3) {
          playComplete(); // They struggled but got it - celebrate!
        } else {
          playCorrect();
        }
        // Auto-advance after working through it
        const isComplete = question?.concept?.completionCriteria === 'complete_all';
        setTimeout(() => {
          if (isFollowUp || conceptMastered) {
            navigate('/dashboard');
          } else if (isComplete) {
            // For complete_all: just go to next seed question, no follow-up
            canGoForward ? goForward() : loadNextQuestion();
          } else {
            loadFollowUpQuestion();
          }
        }, 100);
      } else {
        // Tutor asked follow-up - focus the answer field for response
        setTimeout(() => answerInputRef.current?.focus(), 100);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setTutorLoading(false);
    }
  };

  // Determine what buttons to show after result
  const showFlagForLater = !result?.isCorrect && attemptCount >= 5;
  // isCompleteAllMode is already defined above (line ~1102)
  // Only show Continue/Next buttons AFTER explanation is verified
  // For two_first_try: correct answer (not follow-up, not yet mastered) shows Continue to proceed to follow-up
  // For complete_all: no follow-ups, just advance to next seed question
  const showContinueToFollowUp = result?.isCorrect && !isFollowUp && explanationVerified && !conceptMastered && !isCompleteAllMode;
  // After correct on follow-up OR when concept is mastered, show Next Lesson
  // For complete_all: show Next Lesson only when all questions completed (conceptMastered)
  const showNextLesson = result?.isCorrect && explanationVerified && (isFollowUp || conceptMastered);
  // For complete_all: show Next Question button to advance to next seed (no follow-up)
  const showNextQuestionButton = result?.isCorrect && explanationVerified && isCompleteAllMode && !conceptMastered;
  // Show prompt to explain if result exists but not yet verified
  // needsExplanation available if needed: result && !explanationVerified

  // Enter key to advance: Next Lesson (dashboard), Next Question, or Continue
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      // Don't trigger if user is typing in an input field
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;

      // Show Next Lesson -> go back to dashboard
      if (showNextLesson) {
        navigate('/dashboard');
        return;
      }
      // Wrong answer but explanation verified - can proceed
      if (result && !result.isCorrect && explanationVerified) {
        loadNextQuestion();
        return;
      }
      // For complete_all mode: advance to next seed question (no follow-up)
      if (result?.isCorrect && explanationVerified && isCompleteAllMode && !conceptMastered) {
        canGoForward ? goForward() : loadNextQuestion();
        return;
      }
      // Correct on first question, need follow-up - Continue (two_first_try mode only)
      if (result?.isCorrect && !isFollowUp && !conceptMastered && explanationVerified && !isCompleteAllMode) {
        loadFollowUpQuestion();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showNextLesson, result, explanationVerified, isFollowUp, conceptMastered, isCompleteAllMode, canGoForward, navigate, loadNextQuestion, loadFollowUpQuestion, goForward]);

  if (question?.complete) {
    return (
      <div className="practice-page">
        <header className="header">
          <h1>KillerHomework</h1>
          <Link to="/dashboard" className="btn btn-secondary">Back to Dashboard</Link>
        </header>
        <main className="practice-content">
          <div className="complete-card">
            <h2>Amazing Work!</h2>
            <p>You've mastered all available concepts!</p>
            <div className="session-stats">
              <div>Questions: {questionsAnswered}</div>
              <div>Correct: {correctAnswers}</div>
            </div>
            <Link to="/dashboard" className="btn btn-primary">Back to Dashboard</Link>
          </div>
        </main>
      </div>
    );
  }

  // Calculate lesson completion progress based on completion criteria from DB
  const completionCriteria = question?.concept?.completionCriteria || 'two_first_try';
  const lessonTarget = completionCriteria === 'two_first_try' ? 2 : (question?.concept?.totalQuestions || 1);
  // Use lessonProgress for two_first_try, totalCompleted for complete_all
  const lessonCompleted = conceptMastered
    ? lessonTarget
    : completionCriteria === 'two_first_try'
      ? Math.min(lessonProgress, lessonTarget)
      : (question?.concept?.totalCompleted || 0);

  return (
    <div className="practice-page">
      <header className="header">
        <h1>KillerHomework</h1>
        <div className="header-actions">
          <span className="toggle-label">Speech</span>
          <button
            className="sound-toggle"
            onClick={toggleVoiceOnOff}
            title={speechMode === 'off' ? 'Turn on voice chat' : 'Turn off voice chat'}
          >
            {speechMode === 'off' ? '🔇' : '🔊'}
          </button>
          <span className="toggle-label">Chime</span>
          <button
            className="sound-toggle"
            onClick={toggleSound}
            title={soundEnabled ? 'Chimes on' : 'Chimes off'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          <span className="user-name">{user?.name}</span>
        </div>
      </header>

      <main className={`practice-content ${speechMode !== 'off' ? 'voice-panel-visible' : ''}`}>
        {loading ? (
          <div className="loading-card">
            <p>Loading question...</p>
          </div>
        ) : question ? (
          <div className="practice-layout">
            <div className="question-panel">
              <div className="lesson-header-row">
                <div className="concept-badge">
                  {isFollowUp && <span className="follow-up-badge">Follow-up: </span>}
                  Lesson {question.concept?.lessonNumber}: {question.concept?.title}
                </div>
                <div className="lesson-mode">
                  <span className="lesson-mode-label">To complete:</span> {completionCriteria === 'complete_all' ? `Answer all ${lessonTarget} questions.` : 'Get two right on the first try.'}
                </div>
                <span className="lesson-progress">Completed: {lessonCompleted}/{lessonTarget}</span>
              </div>

              <div className={`question-card ${
                showMeMode ? 'show-me-mode' :
                questionHistory[historyIndex]?.state === 'completed' ? 'state-completed' :
                questionHistory[historyIndex]?.state === 'skipped' ? 'state-skipped' :
                'state-working'
              }`}>
                {showMeMode && (
                  <div className="show-me-banner">
                    SHOW ME - does not count toward lesson completion
                  </div>
                )}
                <h2>{showMeMode ? 'Practice Problem' : `QUESTION ${lessonHistory.slice(0, lessonHistoryIndex + 1).filter(h => !h.isShowMe).length}`}</h2>
                {(() => {
                  const { text, diagrams } = parseDiagramCommands(question.questionText || '');
                  return (
                    <>
                      <p className="question-text"><MathText>{text}</MathText></p>
                      {diagrams.length > 0 && (
                        <div className={diagrams.length > 1 ? 'geometry-diagrams' : ''}>
                          {diagrams.map((diagramProps, i) => (
                            <GeometryDiagram key={i} {...diagramProps} />
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Result card - shows feedback after submission (moved above form) */}
                {result && (
                  <div className={`result-card ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                    {conceptMastered ? (
                      <>
                        <h3>Concept Mastered!</h3>
                        <p>You got 2 in a row on your first attempt. Great work!</p>
                      </>
                    ) : (
                      <>
                        <h3>{result.isCorrect ? 'Correct!' : 'Not quite...'}</h3>
                        {submittedAnswer && (
                          <>
                            <p className="student-answer">You: <MathText>{submittedAnswer}</MathText></p>
                          </>
                        )}
                        <p className="tutor-response">Sudo: <MathText>{result.feedback}</MathText></p>
                      </>
                    )}
                    {result.xpEarned > 0 && <p className="xp-earned">+{result.xpEarned} XP</p>}

                    <div className="result-actions">
                      {/* Show "Continue" for first-attempt correct - navigates forward if exists, else loads follow-up */}
                      {showContinueToFollowUp && (
                        <button
                          className="btn btn-primary"
                          onClick={() => canGoForward ? goForward() : (conceptMastered ? navigate('/dashboard') : loadFollowUpQuestion())}
                          disabled={loadingFollowUp}
                        >
                          {loadingFollowUp ? 'Loading...' : 'Continue'}
                        </button>
                      )}

                      {/* Show "Next Lesson" after correct on follow-up - ONLY after explanation */}
                      {showNextLesson && (
                        <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
                          Next Lesson
                        </button>
                      )}

                      {/* For complete_all mode: advance to next seed question (no follow-up) */}
                      {showNextQuestionButton && (
                        <button className="btn btn-primary" onClick={() => canGoForward ? goForward() : loadNextQuestion()}>
                          Next Question
                        </button>
                      )}

                      {/* Wrong answer: Next requires explanation, Show Me is in button bar */}
                      {!result.isCorrect && (
                        <>
                          {explanationVerified && (
                            <button className="btn btn-primary" onClick={() => loadNextQuestion()}>
                              Next Question
                            </button>
                          )}
                          {showFlagForLater && (
                            <button className="btn btn-warning" onClick={handleFlagForLater}>
                              Flag for Later
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Input form - always shows for tutor chat and navigation */}
                {(!result || showTutor || (result?.isCorrect && lessonHistoryIndex >= 0) || conceptMastered) && (
                  <form
                    onSubmit={(e) => {
                      // Wrong or no result → submit new answer
                      // Correct result → chat with tutor to explain
                      console.log('[form onSubmit] result:', result, 'result.isCorrect:', result?.isCorrect);
                      if (!result || !result.isCorrect) {
                        console.log('[form onSubmit] Calling handleSubmit');
                        handleSubmit(e);
                      } else {
                        console.log('[form onSubmit] Calling sendTutorMessage');
                        sendTutorMessage(e);
                      }
                    }}
                    className="answer-form"
                  >
                    <div className="form-actions">
                      {/* Detect if viewing completed history */}
                      {(() => {
                        const viewingHistory = result?.isCorrect && lessonHistoryIndex >= 0 && questionHistory[historyIndex]?.state === 'completed';
                        const inTutorMode = result && (showTutor || viewingHistory);
                        return (
                          <>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={submitting || tutorLoading || ocrLoading}
                            >
                              {(tutorLoading || submitting) ? 'Sending...' : 'Send'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-icon"
                              onClick={goBack}
                              disabled={submitting || loading || !canGoBack}
                              title="Previous question in this lesson"
                            >
                              ←
                            </button>
                            <button
                              type="button"
                              className="btn btn-icon"
                              onClick={goForward}
                              disabled={submitting || loading || !canGoForward}
                              title="Next question in this lesson"
                            >
                              →
                            </button>
                            {!showMeMode && canShowMe && (
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleShowMe}
                                disabled={submitting || loading}
                                title={`Get a guided walkthrough (${2 - showMeCount} remaining)`}
                              >
                                Show Me
                              </button>
                            )}
                          </>
                        );
                      })()}
                      <button
                        type="button"
                        className="btn btn-secondary camera-btn"
                        onClick={() => openCamera(result && showTutor ? 'chat' : 'answer')}
                        disabled={submitting || tutorLoading || ocrLoading}
                        title="Take photo of your work"
                      >
                        {ocrLoading ? '...' : '📷'}
                      </button>
                    </div>
                    <MathEditor
                      ref={answerInputRef}
                      value={result?.isCorrect ? tutorInput : answerLatex}
                      onChange={result?.isCorrect
                        ? (latex) => setTutorInput(latex)
                        : (latex) => {
                            setAnswerLatex(latex);
                            setAnswer(latexToPlain(latex));
                          }
                      }
                      onSubmit={() => {
                        if (result?.isCorrect) {
                          // If explanation verified and input empty, advance instead of submit
                          if (explanationVerified && !tutorInput.trim()) {
                            if (isFollowUp || conceptMastered) {
                              navigate('/dashboard');
                            } else if (isCompleteAllMode) {
                              // For complete_all: just go to next seed question, no follow-up
                              canGoForward ? goForward() : loadNextQuestion();
                            } else {
                              loadFollowUpQuestion();
                            }
                            return;
                          }
                          if (tutorInput.trim() && !tutorLoading && !ocrLoading) {
                            const form = document.querySelector('.answer-form') as HTMLFormElement | null;
                            if (form) form.requestSubmit();
                          }
                        } else {
                          // Wrong answer with explanation verified + empty input = next question
                          if (explanationVerified && !answer.trim()) {
                            loadNextQuestion();
                            return;
                          }
                          if (answer.trim() && !submitting && !ocrLoading) {
                            const form = document.querySelector('.answer-form') as HTMLFormElement | null;
                            if (form) form.requestSubmit();
                          }
                        }
                      }}
                      placeholder={
                        showMeMode
                          ? "Show your work here... You can write multiple lines to explain your thinking."
                          : conceptMastered
                            ? "Answer... (short answer approved)"
                            : "Answer... (show work)"
                      }
                      showKeyboard={true}
                      disabled={submitting || tutorLoading || ocrLoading}
                    />
                  </form>
                )}

              </div>

              {/* My Work Section - shows captured photo */}
              {capturedImage && (
                <div className="my-work-card">
                  <div className="my-work-header">
                    <h3>My Work</h3>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => setCapturedImage(null)}
                      title="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="my-work-image">
                    <img src={capturedImage} alt="My work" />
                  </div>
                </div>
              )}

              {/* Worked Example Modal */}
              {showWorkedExample && workedExample && (
                <div className="worked-example-card">
                  <h3>Worked Example</h3>
                  <div className="example-steps">
                    {workedExample.steps.map((step, i) => (
                      <div key={i} className="example-step">
                        <MathText>{step}</MathText>
                      </div>
                    ))}
                  </div>
                  <div className="example-answer">
                    <strong>Answer:</strong> <MathText>{workedExample.finalAnswer}</MathText>
                  </div>
                  <button className="btn btn-secondary" onClick={() => setShowWorkedExample(false)}>
                    Close
                  </button>
                </div>
              )}
            

            </div>  {/* End question-panel */}

            {/* Right panel - Sudo Remole conversation log */}
            <div className="history-panel">
              <div className="chat-header">
                <h3>Sudo Remole</h3>
                <div className="chat-header-actions">
                  <Link to="/dashboard" className="btn btn-secondary btn-small">End Lesson</Link>
                </div>
              </div>
              <div className="chat-messages">
                {/* Question as first log entry (strip diagram commands) */}
                {question?.questionText && (
                  <div className="chat-message question">
                    <div className="message-header">
                      <span className="message-sender">Question</span>
                    </div>
                    <p><MathText>{parseDiagramCommands(question.questionText).text}</MathText></p>
                  </div>
                )}
                {/* Tutor messages */}
                {tutorMessages.map((msg, i) => (
                  <div key={i} className={`chat-message ${msg.sender}`}>
                    <div className="message-header">
                      <span className="message-sender">{msg.sender === 'ai' ? 'Sudo' : 'You'}</span>
                      {msg.sender === 'ai' && ttsAvailable && voiceEnabled && (
                        <button
                          className="speak-btn"
                          onClick={() => isSpeaking ? stopSpeaking() : speak(msg.text)}
                          title={isSpeaking ? 'Stop' : 'Listen'}
                        >
                          {isSpeaking ? '⏹️' : '▶️'}
                        </button>
                      )}
                    </div>
                    {msg.image ? (
                      <div className="message-image">
                        <img src={msg.image} alt="Student's work" />
                      </div>
                    ) : (
                      <p><MathText>{msg.text}</MathText></p>
                    )}
                  </div>
                ))}
                {tutorLoading && (
                  <div className="chat-message ai">
                    <span className="message-sender">Sudo</span>
                    <p className="typing">Thinking...</p>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </div>

            {/* Voice Chat Panel - shows in both 🔊 mic and 🎧 headphones modes */}
            {speechMode !== 'off' && (
              <div className="voice-chat-panel">
                <div className={`voice-chat-header ${voiceChatExpanded ? 'expanded' : 'collapsed'}`} onClick={() => setVoiceChatExpanded(!voiceChatExpanded)} style={{ cursor: 'pointer' }}>
                  <div className="voice-chat-title">
                    <span className="voice-chat-chevron">{voiceChatExpanded ? '▲' : '▼'}</span>
                    <h3>Voice Chat</h3>
                    {/* Send button - mic mode only, right of title */}
                    {speechMode === 'listen' && (
                      <button
                        className="voice-send-btn"
                        onClick={handleSendPendingTranscript}
                        disabled={isPaused || voiceMessages.filter(m => m.sender === 'student' && !m.sent).length === 0}
                      >
                        Send
                      </button>
                    )}
                  </div>
                  <div className="voice-status">
                    {isSpeaking ? (
                      <span className="status-speaking">🔊 Sudo speaking...</span>
                    ) : isPaused ? (
                      <button className="voice-mode-toggle" onClick={toggleMicHeadphones} title={speechMode === 'listen' ? 'Switch to headphones' : 'Switch to mic'}>
                        {speechMode === 'listen' ? '🎤' : '🎧'}
                      </button>
                    ) : isStreaming ? (
                      <button className="voice-mode-toggle listening" onClick={toggleMicHeadphones} title={speechMode === 'listen' ? 'Switch to headphones' : 'Switch to mic'}>
                        {speechMode === 'listen' ? '🎤' : '🎧'} Listening...
                      </button>
                    ) : (
                      <span className="status-idle">Connecting...</span>
                    )}
                    {/* Pause/Resume - both modes */}
                    {isStreaming && (
                      <button
                        className={`voice-pause-btn ${isPaused ? 'paused' : ''}`}
                        onClick={isPaused ? handleVoiceResume : togglePauseStreaming}
                      >
                        {isPaused ? 'Resume' : 'Pause'}
                      </button>
                    )}
                  </div>
                </div>
                {voiceChatExpanded && (
                <div className="voice-messages">
                  {voiceMessages.map((msg, i) => (
                    <div key={i} className={`voice-utterance ${msg.sender} ${isPaused && msg.sender === 'student' ? 'editable' : ''}`}>
                      {/* Delete button - only when paused, only for student messages */}
                      {isPaused && msg.sender === 'student' && (
                        <button
                          className="chop-delete-btn"
                          onClick={() => setVoiceMessages(prev => prev.filter((_, idx) => idx !== i))}
                          title="Delete"
                        >
                          ×
                        </button>
                      )}
                      <span className="utterance-sender">{msg.sender === 'sudo' ? 'Sudo' : 'You'}:</span>
                      {/* Editable when paused (student messages only) */}
                      {isPaused && msg.sender === 'student' ? (
                        <input
                          className="utterance-edit"
                          value={msg.text}
                          onChange={(e) => {
                            const newText = e.target.value;
                            setVoiceMessages(prev => prev.map((m, idx) =>
                              idx === i ? { ...m, text: newText } : m
                            ));
                          }}
                        />
                      ) : (
                        <span className="utterance-text">{msg.text}</span>
                      )}
                    </div>
                  ))}
                  {/* Show partial/pending transcript while speaking */}
                  {(partialTranscript || pendingTranscript) && (
                    <div className="voice-utterance student partial">
                      <span className="utterance-sender">You:</span>
                      <span className="utterance-text">
                        {pendingTranscript}{pendingTranscript && partialTranscript ? ' ' : ''}{partialTranscript}...
                      </span>
                    </div>
                  )}
                  <div ref={voiceChatEndRef} />
                </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="error-card">
            <p>Failed to load question. Please try again.</p>
            <button className="btn btn-primary" onClick={() => loadNextQuestion()}>Retry</button>
          </div>
        )}
      </main>

      {/* Question ID indicator - subtle, bottom right */}
      {question?.questionId && (
        <div className="question-id-indicator">
          Q#{question.questionId}
        </div>
      )}

      {/* PWA Camera Capture Overlay */}
      {showCamera && (
        <CameraCapture
          onCapture={handlePWACameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Waiting for Phone Modal (PC only) */}
      {showWaitingForPhone && (
        <div className="phone-waiting-overlay">
          <div className="phone-waiting-modal">
            <h3>Take Photo on Phone</h3>
            <p>Open <strong>/camera</strong> on your phone to take a photo of your work.</p>
            <p className="waiting-status">Waiting for photo...</p>
            <div className="phone-waiting-actions">
              <button className="btn btn-secondary" onClick={useWebcam}>
                Use Webcam Instead
              </button>
              <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
                Upload File
              </button>
              <button className="btn btn-outline" onClick={cancelWaitingForPhone}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for upload fallback */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
    </div>
  );
}
